import React, { useMemo, memo, Suspense, lazy } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'

import Icon from 'components/Shared/Icon'
import Button from 'components/Shared/Button'
import { GroupedAccordion } from 'components/Shared/Accordion'

// Import custom hooks
import { useAuditData, useScreenNavigation } from './hooks'

// Import components
import { LoadingMessage, ErrorContent } from './components/shared'

import './x-ray.scss'

// Lazy load accordion sections for better bundle splitting
const NestedListsSection = lazy(() => import('./components/NestedListsSection'))
const ComplexitySection = lazy(() => import('./components/ComplexitySection'))
const CacheHitRatiosSection = lazy(
  () => import('./components/CacheHitRatiosSection')
)
const OrphanedScreensSection = lazy(
  () => import('./components/OrphanedScreensSection')
)
const LiveRowCountsSection = lazy(
  () => import('./components/LiveRowCountsSection')
)
const ComponentsSection = lazy(() => import('./components/ComponentsSection'))

// Lightweight loading component for accordion sections
const AccordionLoadingFallback: React.FC = () => (
  <div
    className="accordion-loading"
    style={{ padding: '20px', textAlign: 'center' }}
  >
    <div>Loading section...</div>
  </div>
)

interface AuditingPanelProps extends RouteComponentProps {
  appId: string
}

const AuditingPanel: React.FC<AuditingPanelProps> = ({ appId }) => {
  // Use custom hooks for cleaner code
  const {
    auditData,
    isAuditing,
    isLoading,
    isRefreshing,
    error,
    progress,
    fetchAuditData,
  } = useAuditData(appId)

  const { handleScreenClick, handleKeyDown } = useScreenNavigation(appId)

  const introSection = useMemo(
    () => (
      <div className="x-ray-intro">
        <h3>Welcome to Adalo X-Ray</h3>
        <p>
          Adalo X-Ray is an experimental tool intended to help Makers build more
          performant apps. We welcome your feedback! Email us at{' '}
          <a href="mailto:support@adalo.com">support@adalo.com</a> with comments
          and questions.
        </p>
        <Button
          onClick={() => fetchAuditData(false)}
          teal
          icon="search"
          medium
          disabled={isLoading}
        >
          {isLoading ? 'Starting Audit...' : 'Start Audit'}
        </Button>
      </div>
    ),
    [fetchAuditData, isLoading]
  )

  const auditContent = useMemo(() => {
    if (error) {
      return (
        <div className="x-ray-content">
          <ErrorContent
            error={error}
            isRefreshing={isRefreshing}
            onRetry={() => {
              fetchAuditData(true).catch(err => {
                console.error('Error fetching audit data:', err)
              })
            }}
          />
        </div>
      )
    }

    if (isLoading || isRefreshing) {
      return (
        <div className="x-ray-content">
          <LoadingMessage progress={progress} />
        </div>
      )
    }

    return (
      <div className="x-ray-content">
        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Nested Lists</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <NestedListsSection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Screen Complexity</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <ComplexitySection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Component Analysis</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <ComponentsSection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Orphaned Screens</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <OrphanedScreensSection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />
        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Database Tables</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <LiveRowCountsSection auditData={auditData} appId={appId} />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Cache Hit Ratios</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <CacheHitRatiosSection auditData={auditData} />
            </Suspense>
          )}
        />

        <div className="x-ray-intro x-ray-button-container">
          <Button
            onClick={() => fetchAuditData(true)}
            teal
            icon="refresh"
            medium
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing Audit...' : 'Audit Again'}
          </Button>
        </div>

        <div className="x-ray-feedback">
          <Icon type="contact-email" />
          Have a feedback? Email us at
          <a href="mailto:xray@adalo.com">xray@adalo.com</a>
        </div>
      </div>
    )
  }, [
    auditData,
    handleScreenClick,
    handleKeyDown,
    fetchAuditData,
    isLoading,
    isRefreshing,
    error,
    progress,
  ])

  return (
    <div className="x-ray-audit-wrapper">
      <div className="left-panel-header">
        <h2 className="left-panel-title">
          <Icon type="link" />
          Audit Your App
        </h2>
      </div>

      {!isAuditing ? introSection : auditContent}
    </div>
  )
}

export default memo(withRouter(AuditingPanel))
